import type {
  HexToRGBOptions,
  HexToRGBOptionsArray,
  HexToRGBOptionsCss,
  HexToRGBOptionsObject,
} from './types/hex-to-rgb.options';
import type { RGBArray } from './types/rgb-array.type';
import type { RGB } from './types/rgb.type';

import { rgbaToCss } from './rgba-to-css';

export function hexToRGB(value: string, options: HexToRGBOptionsArray): RGBArray;
export function hexToRGB(value: string, options: HexToRGBOptionsCss): string;
export function hexToRGB(value: string, options?: HexToRGBOptionsObject): RGB;
/**
 * Converts a hexadecimal color string to its RGB(A) representation.
 *
 * Supports multiple output formats based on the provided options:
 * - Returns an array `[red, green, blue, alpha]` if `format: 'array'` is specified.
 * - Returns a CSS color string if `format: 'css'` is specified.
 * - Returns an object `{ red, green, blue, alpha }` by default or if `format: 'object'` is specified.
 *
 * Handles hex strings of length 3, 4, 6, or 8 (with or without leading `#`).
 * Automatically expands shorthand hex codes (e.g., `#abc` → `#aabbcc`).
 * If alpha is not specified in the hex code, defaults to full opacity.
 * The alpha value can be overridden via options.
 *
 * @param value - The hex color string to convert (e.g., `#ffcc00`, `abc`, `#1234`).
 * @param options - Options to control the output format and alpha value.
 * @returns The color in the requested format: array, CSS string, or object.
 * @throws {Error} If the input hex string is not a valid color.
 */
export function hexToRGB(value: string, options: HexToRGBOptions = {}): RGB | RGBArray | string {
  let hex = value.replace(/^#/, '').trim();

  if (![3, 4, 6, 8].includes(hex.length)) {
    throw new Error(`Invalid hex color: ${value}`);
  }

  if (hex.length === 3 || hex.length === 4) {
    hex = hex
      // eslint-disable-next-line unicorn/prefer-spread
      .split('')
      .map((char) => {
        return char + char;
      })
      .join('');
  }

  if (hex.length === 6) {
    hex += 'ff';
  }

  const [red, green, blue, alpha] = (hex.match(/\w\w/g)?.map((match) => {
    return Number.parseInt(match, 16);
  }) ?? [0, 0, 0, 255]) as [number, number, number, number];

  const { alpha: alphaFromOptions, format } = options;

  const newAlpha =
    typeof alphaFromOptions === 'number' ? Math.min(1, Math.max(0, alphaFromOptions)) : +(alpha / 255).toFixed(3);

  if (format === 'array') {
    return [red, green, blue, newAlpha];
  }

  const rgba: RGB = { alpha: newAlpha, blue, green, red };

  if (format === 'css') {
    return rgbaToCss(rgba);
  }

  return rgba;
}
